import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useMessageRoute, useUpdateMessageRoute, useDeleteMessageRoute } from '../hooks/useMessageRoutes';
import { MessageRouteForm } from '../components/messageRoutes/MessageRouteForm';
import MessageRouteFlowDiagram from '../components/messageRoutes/MessageRouteFlowDiagram';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { UserName } from '../components/ui/UserName';

export default function MessageRouteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: route, isLoading, error } = useMessageRoute(id);
  const updateMessageRoute = useUpdateMessageRoute();
  const deleteMessageRoute = useDeleteMessageRoute();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  const handleSubmit = async (data: Record<string, unknown>) => {
    await updateMessageRoute.mutateAsync({ id: id!, data: data as Parameters<typeof updateMessageRoute.mutateAsync>[0]['data'] });
    setIsEditing(false);
  };

  const handleStartEditName = () => {
    setEditedName(route?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === route?.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await updateMessageRoute.mutateAsync({ id: id!, data: { name: editedName.trim() } as Parameters<typeof updateMessageRoute.mutateAsync>[0]['data'] });
      setIsEditingName(false);
    } catch (err) {
      console.error('Failed to update name:', err);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading message route...</div>
      </div>
    );
  }

  if (error || !route) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading message route. Please try again.</div>
      </div>
    );
  }

  const cooldownSeconds = route.cooldownMs > 0 ? (route.cooldownMs / 1000).toFixed(1).replace(/\.0$/, '') : '0';

  return (
    <div>
      {/* Custom Header with Inline Name Edit */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className="text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-indigo-500 focus:outline-none focus:border-indigo-600 px-1"
              />
              <button
                type="button"
                onClick={handleSaveName}
                disabled={updateMessageRoute.isPending}
                className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                title="Save"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleCancelEditName}
                disabled={updateMessageRoute.isPending}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Cancel"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {route.name}
              </h1>
              {!isEditing && (
                <button
                  type="button"
                  onClick={handleStartEditName}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Edit name"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Message Route #{route.id} · Created {new Date(route.createdAt).toLocaleDateString()} · Updated {new Date(route.updatedAt).toLocaleDateString()}
          </p>
        </div>
        {!isEditing && (
          <div className="mt-4 flex md:ml-4 md:mt-0 space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
              Edit
            </button>
            {/* The one delete affordance (task 864). The two-step header
                swap this replaces turned Delete into Confirm Delete in
                place — a second click on the same spot, which is the
                easiest confirmation in the world to give by accident. */}
            <DeleteResourceControl
              flag="innovate.fe.message-routes.delete"
              entityLabel="message route"
              name={route.name}
              id={route.id}
              variant="button"
              body={<p>Messages arriving on it stop being routed.</p>}
              onDelete={() => deleteMessageRoute.mutateAsync(route.id)}
              onDeleted={() => navigate('/message-routes')}
            />
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <MessageRouteForm
            mode="edit"
            initialData={route}
            onSubmit={handleSubmit}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Flow Diagram */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <ArrowPathIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Routing Flow</h2>
              </div>
              <MessageRouteFlowDiagram route={route} />
            </div>

            {/* Matching Conditions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Conditions</h2>
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium w-40">Channel Pattern</td>
                    <td className="py-2 font-mono text-gray-900 dark:text-white">{route.channelPattern}</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Entity</td>
                    <td className="py-2 text-gray-900 dark:text-white">{route.entity || <span className="text-gray-400 dark:text-gray-500 italic">any</span>}</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Operations</td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {route.operations.map((op) => (
                          <span
                            key={op}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
                          >
                            {op}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Source</td>
                    <td className="py-2 text-gray-900 dark:text-white">{route.source || <span className="text-gray-400 dark:text-gray-500 italic">any</span>}</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Exclude Sources</td>
                    <td className="py-2">
                      {route.excludeSources.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {route.excludeSources.map((src) => (
                            <span
                              key={src}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                            >
                              {src}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">none</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Routing Behavior */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Routing Behavior</h2>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{route.priority}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        route.active
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {route.active ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Stop on Match</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{route.stopOnMatch ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cooldown</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {route.cooldownMs} ms ({cooldownSeconds}s)
                  </dd>
                </div>
              </dl>
            </div>

            {/* Target Endpoint */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Target Endpoint</h2>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Endpoint Path</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{route.endpointPath}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Method</dt>
                  <dd className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-sm font-mono font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {route.endpointMethod}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Input Mapping</dt>
                  <dd>
                    {Object.keys(route.inputMapping).length === 0 ? (
                      <span className="text-sm text-gray-400 dark:text-gray-500 italic">No input mapping configured</span>
                    ) : (
                      <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3 overflow-x-auto text-gray-900 dark:text-gray-100">
                        {JSON.stringify(route.inputMapping, null, 2)}
                      </pre>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Audit</h2>
              </div>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(route.createdAt).toLocaleString()}
                  </dd>
                  {route.createdBy && (
                    <dd className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      by <UserName userId={route.createdBy} />
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(route.updatedAt).toLocaleString()}
                  </dd>
                  {route.updatedBy && (
                    <dd className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      by <UserName userId={route.updatedBy} />
                    </dd>
                  )}
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
